'use es6';

import { useMemo } from 'react';
import { useTheme } from 'styled-components';
import { useSelector, useDispatch } from 'react-redux';
import FormattedMessage from 'I18n/components/FormattedMessage';
import { FloatingAlertStore } from 'foundations-components/transitional/components/FloatingAlertList';
import { getDisplayParamsSubAppKey } from 'reporting-data/report/configReportGetters';
import AsyncSingleReportViewerFullPage from 'reporting-enablement/components/singleReportViewer/AsyncSingleReportViewerFullPage';
import { getSalesAnalyticsEditorOverrides, addEditorsToSavedSalesAnalyticsReport } from 'sales-analytics-lib/utils/savedReportEditorUtils';
import { selectViewReportProblem } from 'dashboard-lib/public/userInfo/permissions/reportActions';
import { trackEvent, EVENTS } from '../lib/usageTracker';
import { clearReportsFilters, getReportCounts, updateReportSucceeded, getReports } from '../actions/reportsActions';
import { getDashboards } from '../actions/dashboardsActions';
import { CUSTOM_REPORT_COUNT, CUSTOM_REPORT_LIMIT, MARKETING_HUB_UPGRADE_REQUIRED, RESTRICTED_FIELD_LEVEL_PROPERTIES } from 'dashboard-lib/private/action-problem-solution/params/all-params';
import { selectCustomReportsCount } from 'dashboard-lib/public/reports';
import { selectCustomReportsLimit } from 'dashboard-lib/public/userInfo/limits';
import { getAttributionReportScopeReq, selectHasCustomReportingAcccess } from 'dashboard-lib/public/userInfo/scopes';
import { getViewReportError } from 'dashboard-components/disabled-message/actions/view-report';
import { UPGRADE_POINT_LOCATIONS } from 'dashboard-lib/public/upgradePointKeys';
import ReportErrorMessage from './ReportErrorMessage';
import useNormalizedReport from '../lib/useNormalizedReport';
import { getRestrictedFieldLevelProperties } from 'reporting-action-components/data/schemas/reportSchema';
import { selectRawUserInfo } from 'dashboard-lib/public/userInfo';
import { getListingPageContentAreaHeight } from '../containers/CustomListingPageWrapper';
import { jsx as _jsx } from "react/jsx-runtime";
const ReportViewer = props => {
  const {
    report,
    onClose
  } = props;
  const theme = useTheme();
  const dispatch = useDispatch();
  const normalizedReport = useNormalizedReport({
    report,
    onError: () => {
      FloatingAlertStore.addAlert({
        message: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "reports.errors.openReportNormalizationError"
        }),
        type: 'danger'
      });
    }
  });
  const customReportCount = useSelector(selectCustomReportsCount);
  const customReportsLimit = useSelector(selectCustomReportsLimit);
  const hasBuilderAccess = useSelector(selectHasCustomReportingAcccess);
  const viewReportProblem = useSelector(selectViewReportProblem(report));
  const attributionScopeRequirement = getAttributionReportScopeReq(report);
  const restrictedFieldLevelProperties = getRestrictedFieldLevelProperties(report);
  const ViewReportError = getViewReportError({
    [CUSTOM_REPORT_COUNT]: customReportCount,
    [CUSTOM_REPORT_LIMIT]: customReportsLimit,
    builderUpgradeLocation: UPGRADE_POINT_LOCATIONS.REPORT_LIST_VIEW,
    [MARKETING_HUB_UPGRADE_REQUIRED]: attributionScopeRequirement,
    [RESTRICTED_FIELD_LEVEL_PROPERTIES]: restrictedFieldLevelProperties
  }, viewReportProblem);
  const onUpdateSuccess = ({
    report: updatedReport,
    shouldKeepViewerOpen = false
  }) => {
    if (!shouldKeepViewerOpen) {
      dispatch(updateReportSucceeded(updatedReport));
      dispatch(getReports());
      onClose();
    }
  };
  const onSaveAsSuccess = ({
    shouldKeepViewerOpen
  }) => {
    dispatch(getDashboards());
    dispatch(clearReportsFilters());
    dispatch(getReportCounts());
    if (!shouldKeepViewerOpen) {
      onClose();
    }
  };
  const userInfo = useSelector(selectRawUserInfo);
  const {
    overrides: editorOverrides,
    reportWithEditors
  } = useMemo(() => {
    if (!userInfo || !normalizedReport || !getDisplayParamsSubAppKey(normalizedReport)) {
      return {
        overrides: undefined,
        reportWithEditors: normalizedReport
      };
    }
    const gates = (userInfo === null || userInfo === void 0 ? void 0 : userInfo.gates) || [];
    const overrides = getSalesAnalyticsEditorOverrides(normalizedReport);
    const updatedReport = addEditorsToSavedSalesAnalyticsReport(normalizedReport, gates);
    return {
      overrides,
      reportWithEditors: updatedReport
    };
  }, [normalizedReport, userInfo]);
  if (!reportWithEditors) {
    return null;
  }
  return /*#__PURE__*/_jsx(AsyncSingleReportViewerFullPage, {
    report: reportWithEditors,
    onClose: onClose,
    onClickEditReport: () => trackEvent(EVENTS.clickEditReport, {
      source: 'REPORT_VIEWER'
    }),
    editorOverrides: editorOverrides,
    builderAccess: hasBuilderAccess,
    track: trackEvent,
    reportErrorRenderer: ReportErrorMessage,
    onSaveAsSuccess: onSaveAsSuccess,
    onUpdateSuccess: onUpdateSuccess,
    upgradeLocation: UPGRADE_POINT_LOCATIONS.REPORT_LIST_EDIT,
    viewReportDeniedMessage: ViewReportError && /*#__PURE__*/_jsx(ViewReportError, {}),
    userInfo: userInfo,
    height: getListingPageContentAreaHeight(theme)
  });
};
export default ReportViewer;